import React, { useRef } from 'react';
import { 
  Calendar as CalendarIcon, 
  Kanban, 
  Table, 
  GitCommitHorizontal, 
  BarChart3, 
  Upload, 
  Download, 
  RotateCcw, 
  Sun, 
  Moon, 
  Sparkles,
  FileSpreadsheet,
  Save,
  CheckCircle2,
  Lock,
  Globe
} from 'lucide-react';
import { ViewMode } from '../types/calendar';

interface HeaderProps {
  currentView: ViewMode;
  onViewChange: (view: ViewMode) => void;
  totalPosts: number;
  filteredCount: number;
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onFileUpload: (file: File) => void;
  onOpenLocalFile: () => void;
  onSaveToLocalFile: () => void;
  linkedFileName: string | null;
  needsPermission?: boolean;
  onRequestPermission?: () => void;
  onResetData: () => void;
  onExportData: () => void;
  remoteCsvUrl?: string | null;
  isReadOnly?: boolean;
  onOpenUrlModal?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentView,
  onViewChange,
  totalPosts,
  filteredCount,
  theme,
  onToggleTheme,
  onFileUpload,
  onOpenLocalFile,
  onSaveToLocalFile,
  linkedFileName,
  needsPermission = false,
  onRequestPermission,
  onResetData,
  onExportData,
  remoteCsvUrl,
  isReadOnly = false,
  onOpenUrlModal,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileUpload(file);
    }
  };

  const navItems: { id: ViewMode; label: string; icon: React.ReactNode }[] = [
    { id: 'calendar', label: 'Calendar', icon: <CalendarIcon size={16} /> },
    { id: 'kanban', label: 'Kanban Board', icon: <Kanban size={16} /> },
    { id: 'table', label: 'Data Grid', icon: <Table size={16} /> },
    { id: 'timeline', label: 'Phase Timeline', icon: <GitCommitHorizontal size={16} /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={16} /> },
  ];

  return (
    <header style={{
      background: 'var(--bg-surface-glass)',
      backdropFilter: 'blur(12px)',
      borderBottom: '1px solid var(--border-color)',
      position: 'sticky',
      top: 0,
      zIndex: 40,
      padding: '0.85rem 1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.85rem',
    }}>
      {/* Top Row: Title, File Controls & Theme */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem',
      }}>
        {/* Logo & Brand Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          }}>
            <Sparkles size={22} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <h1 style={{ 
                fontFamily: 'var(--font-heading)',
                fontSize: '1.25rem', 
                fontWeight: 700, 
                letterSpacing: '-0.02em',
                color: 'var(--text-main)',
              }}>
                Kidz365 Campaign Visualizer
              </h1>
              {remoteCsvUrl ? (
                <span className="badge" style={{
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: '#3b82f6',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                }} title={remoteCsvUrl}>
                  <Globe size={13} />
                  Read-Only (Public URL)
                </span>
              ) : linkedFileName ? (
                <span className="badge" style={{
                  background: needsPermission ? 'rgba(245, 158, 11, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                  color: needsPermission ? '#f59e0b' : '#10b981',
                  border: `1px solid ${needsPermission ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`
                }}>
                  {needsPermission ? <Lock size={13} /> : <CheckCircle2 size={13} />}
                  {linkedFileName} {needsPermission ? '(Permission Needed)' : '(Live Sync)'}
                </span>
              ) : (
                <span className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}>
                  Internal Tool
                </span>
              )}
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Social Media Operations • Showing <strong>{filteredCount}</strong> of <strong>{totalPosts}</strong> posts
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            accept=".csv" 
            style={{ display: 'none' }} 
          />

          {/* Load Public CSV URL Button */}
          {onOpenUrlModal && (
            <button
              onClick={onOpenUrlModal}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.45rem 0.85rem',
                borderRadius: '8px',
                border: remoteCsvUrl ? '1px solid #3b82f6' : '1px solid var(--border-color)',
                background: remoteCsvUrl ? 'rgba(59, 130, 246, 0.15)' : 'var(--bg-surface)',
                color: remoteCsvUrl ? '#3b82f6' : 'var(--text-main)',
                fontSize: '0.85rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
              }}
              title="Load or change a public CSV file URL"
            >
              <Globe size={15} />
              {remoteCsvUrl ? 'Change Public URL' : 'Load Public URL'}
            </button>
          )}

          {/* Direct Local File System Picker (Native Edit & Save) */}
          <button
            onClick={onOpenLocalFile}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              border: '1px solid var(--accent-primary)',
              background: 'var(--accent-glow)',
              color: 'var(--accent-primary)',
              fontSize: '0.85rem',
              fontWeight: 600,
              transition: 'all 0.2s ease',
            }}
            title="Open local CSV file with direct read/write permission"
          >
            <FileSpreadsheet size={15} />
            {linkedFileName ? 'Change Local CSV' : 'Open Local CSV'}
          </button>

          {/* Reconnect Permission Button if restored from IndexedDB */}
          {needsPermission && onRequestPermission && !remoteCsvUrl && (
            <button
              onClick={onRequestPermission}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.45rem 0.85rem',
                borderRadius: '8px',
                border: '1px solid #f59e0b',
                background: 'rgba(245, 158, 11, 0.15)',
                color: '#f59e0b',
                fontSize: '0.85rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
              }}
              title="Click to grant write permission to your local file"
            >
              <Lock size={15} />
              Enable Live Sync
            </button>
          )}

          {/* If a local file is linked, show Save to File button */}
          {linkedFileName && !needsPermission && !remoteCsvUrl && (
            <button
              onClick={onSaveToLocalFile}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.45rem 0.85rem',
                borderRadius: '8px',
                border: '1px solid #10b981',
                background: 'rgba(16, 185, 129, 0.15)',
                color: '#10b981',
                fontSize: '0.85rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
              }}
              title="Save current changes directly back to your local file"
            >
              <Save size={15} />
              Save to File
            </button>
          )}

          {/* Standard Upload Fallback */}
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-main)',
              fontSize: '0.85rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            title="Upload CSV via browser upload"
          >
            <Upload size={15} />
            Upload
          </button>

          <button
            onClick={onResetData}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-muted)',
              fontSize: '0.85rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            title="Reset to default Kidz365 CSV dataset"
          >
            <RotateCcw size={15} />
            Reset
          </button>

          <button
            onClick={onExportData}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-main)',
              fontSize: '0.85rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
            }}
            title="Export currently filtered posts to CSV download"
          >
            <Download size={15} />
            Export CSV
          </button>

          <button
            onClick={onToggleTheme}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              color: 'var(--text-main)',
              transition: 'all 0.2s ease',
            }}
            title={theme === 'light' ? 'Switch to Dark Theme' : 'Switch to Light Theme'}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          </button>
        </div>
      </div>

      {/* Bottom Row: View Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', overflowX: 'auto', paddingBottom: '0.1rem' }}>
        {navItems.map(item => {
          const isActive = currentView === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onViewChange(item.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.45rem',
                padding: '0.45rem 0.9rem',
                borderRadius: '8px',
                border: 'none',
                background: isActive ? 'var(--accent-primary)' : 'transparent',
                color: isActive ? '#ffffff' : 'var(--text-muted)',
                fontSize: '0.85rem',
                fontWeight: isActive ? 600 : 500,
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              {item.icon}
              {item.label}
            </button>
          );
        })}
      </div>
    </header>
  );
};
